import { Component } from 'react'
import styled from 'styled-components'
import { Overlay, ModalContainer, ModalHeader as Header, ModalTitle as Title, CloseBtn as CloseButton, ModalBody as Body, ModalFooter as Footer } from './ModalShell'
import { X, Check } from 'lucide-react'
import { ContentStyle } from './index'
import { Button } from '../Library/Components'

/**
 * QuickAddModal - Componente Modal riutilizzabile per aggiungere rapidamente nuovi elementi
 *
 * @param {boolean} isOpen - Controlla la visibilità del modal
 * @param {function} onClose - Callback chiamata quando il modal viene chiuso
 * @param {function} onSave - Callback chiamata quando si salva (riceve object con valori dei campi)
 * @param {string} title - Titolo del modal
 * @param {array} fields - Array di definizioni campi
 * @param {boolean} isLoading - Mostra stato di caricamento sui bottoni
 */

// Styled Components
const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 0.5rem;
`

const HelperText = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.text.secondary};
  opacity: 0.8;
  margin: 0.375rem 0 0 0;
`

export default class QuickAddModal extends Component {
  constructor (props) {
    super(props)

    // Inizializza state dinamicamente basandosi sui fields
    const initialValues = {}
    props.fields.forEach(field => {
      initialValues[field.name] = ''
    })

    this.state = {
      values: initialValues
    }
  }

  componentDidUpdate (prevProps) {
    // Reset valori quando il modal si apre
    if (this.props.isOpen && !prevProps.isOpen) {
      const initialValues = {}
      this.props.fields.forEach(field => {
        initialValues[field.name] = ''
      })
      this.setState({ values: initialValues })
    }
  }

  handleChange = (fieldName, value) => {
    this.setState(prevState => ({
      values: {
        ...prevState.values,
        [fieldName]: value
      }
    }))
  }

  handleSave = () => {
    const { values } = this.state
    const { onSave } = this.props

    // Chiama il callback con i valori
    if (onSave) {
      onSave(values)
    }
  }

  handleClose = () => {
    const { onClose } = this.props
    if (onClose) {
      onClose()
    }
  }

  handleBackdropClick = () => {
    this.handleClose()
  }

  handleModalClick = (e) => {
    // Previeni la chiusura quando si clicca sul modal stesso
    e.stopPropagation()
  }

  render () {
    const { isOpen, title, fields, isLoading } = this.props
    const { values } = this.state

    if (!isOpen) {
      return null
    }

    return (
      <>
        <Overlay onClick={this.handleBackdropClick} />

        <ModalContainer $width="450px" onClick={this.handleModalClick}>
          <Header>
            <Title>{title}</Title>
            <CloseButton onClick={this.handleClose} disabled={isLoading}>
              <X size={20} />
            </CloseButton>
          </Header>

          <Body $gap="1.25rem">
            {fields.map((field, index) => (
              <FieldContainer key={field.name || index}>
                <Label>{field.label}</Label>
                <ContentStyle.Text
                  type={field.type || 'text'}
                  placeholder={field.placeholder || ''}
                  value={values[field.name] || ''}
                  onChange={(e) => this.handleChange(field.name, e.target.value)}
                  style={{ width: '100%' }}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  disabled={isLoading}
                />
                {field.helperText && (
                  <HelperText>{field.helperText}</HelperText>
                )}
              </FieldContainer>
            ))}
          </Body>

          <Footer>
            <Button
              type='secondary'
              outline='true'
              onClick={this.handleClose}
              disabled={isLoading}
            >
              Annulla
            </Button>
            <Button
              type='success'
              onClick={this.handleSave}
              disabled={isLoading}
            >
              <Check size={18} /> {isLoading ? 'Salvataggio...' : 'Salva'}
            </Button>
          </Footer>
        </ModalContainer>
      </>
    )
  }
}
